<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import AssistantBasicSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantBasicSettingsForm.vue';
import AssistantSystemSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantSystemSettingsForm.vue';
import AssistantControlItems from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantControlItems.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import ConfigWithAiDialog from 'dashboard/components-next/captain/pageComponents/assistant/ConfigWithAiDialog.vue';
import OnboardingChecklist from 'dashboard/components-next/captain/pageComponents/assistant/OnboardingChecklist.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DicaDoCampo from 'dashboard/components-next/captain/pageComponents/assistant/DicaDoCampo.vue';
import AbaOndeAtua from 'dashboard/components-next/captain/pageComponents/assistant/tabs/AbaOndeAtua.vue';
import AbaOQuePodeFazer from 'dashboard/components-next/captain/pageComponents/assistant/tabs/AbaOQuePodeFazer.vue';
import AbaOQueSabe from 'dashboard/components-next/captain/pageComponents/assistant/tabs/AbaOQueSabe.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { isAdmin } = useAdmin();

const isCaptainV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);
const route = useRoute();
const router = useRouter();
const store = useStore();

const deleteAssistantDialog = ref(null);
const configWithAiDialog = ref(null);
const isCloning = ref(false);

const handleConfigWithAi = () => configWithAiDialog.value?.open();

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const assistants = useMapGetter('captainAssistants/getRecords');
// Só mostra o spinner enquanto o assistente ainda não chegou. Um refetch
// disparado por uma aba (ex.: o painel de ações) ligava fetchingItem, o
// PageLayout trocava o corpo pelo spinner, a aba desmontava, remontava e
// buscava de novo: laço infinito (QA da v981, 181 requisições em 20 s).
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const isFetching = computed(
  () => uiFlags.value.fetchingItem && !assistant.value
);

const controlItems = computed(() => {
  return [
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guardrails_index',
    },
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guidelines_index',
    },
    {
      name: 'Materiais',
      description:
        'Arquivos que o agente envia na conversa (prova social, demonstrativo, contrato).',
      routeName: 'captain_assistants_materials_index',
    },
    {
      name: 'Painel da IA',
      description:
        'Métricas do agente e os erros reportados pelos atendentes (correções).',
      routeName: 'captain_assistants_ai_panel_index',
      // Endpoints de métricas/feedbacks são admin-only — escondemos o item dos
      // não-admins para não levá-los a uma página em branco (a rota também é
      // restrita a admin, então um acesso direto cai no redirect de sem-acesso).
      adminOnly: true,
    },
    {
      name: 'Ações do agente',
      description:
        'Escolha quais ações o agente pode executar (FAQ, agendar, mover Kanban, enviar material, calcular economia, etc.).',
      routeName: 'captain_assistants_tool_selection_index',
    },
    {
      name: 'Campos da IA',
      description:
        'Escolha campos personalizados e diga quando a IA deve preenchê-los na conversa (ex.: valor da conta, distribuidora, interesse).',
      routeName: 'captain_assistants_field_rules_index',
    },
    {
      name: 'Regras de roteamento',
      description:
        'Troque o assistente automaticamente quando uma etiqueta é aplicada na conversa ou o card entra numa etapa do Kanban (encaminha para vendas/suporte).',
      routeName: 'captain_assistants_routing_rules_index',
    },
  ].filter(item => !item.adminOnly || isAdmin.value);
});

// 5 abas guiadas (02/09/2026, mockup aprovado pelo Lucas). Cada aba é uma
// pergunta de quem configura. Nesta fatia a aba "Quem é" e a "Técnico" vivem
// aqui; "Onde atua", "O que pode fazer" e "O que sabe" levam para as telas que
// já existem. Técnico só para administrador.
const ABAS = [
  { chave: 'QUEM_E', aba: 'quem-e' },
  { chave: 'ONDE_ATUA', aba: 'onde-atua' },
  { chave: 'O_QUE_PODE_FAZER', aba: 'o-que-pode-fazer' },
  { chave: 'O_QUE_SABE', aba: 'o-que-sabe' },
  { chave: 'TECNICO', aba: 'tecnico', adminOnly: true },
];
const abasVisiveis = computed(() =>
  ABAS.filter(a => !a.adminOnly || isAdmin.value)
);
const abaAtiva = ref(
  abasVisiveis.value.some(a => a.aba === route.query.aba)
    ? route.query.aba
    : 'quem-e'
);
const indiceAbaAtiva = computed(() =>
  Math.max(
    0,
    abasVisiveis.value.findIndex(a => a.aba === abaAtiva.value)
  )
);
const tabs = computed(() =>
  abasVisiveis.value.map(a => ({
    label: t(`CAPTAIN.ABAS.${a.chave}`),
    aba: a.aba,
  }))
);
const trocarAba = tab => {
  const alvo = ABAS.find(a => a.aba === tab.aba);
  if (!alvo) return;
  if (alvo.rota) {
    router.push({
      name: alvo.rota,
      params: {
        accountId: route.params.accountId,
        assistantId: assistantId.value,
      },
    });
    return;
  }
  abaAtiva.value = alvo.aba;
  router.replace({ query: { ...route.query, aba: alvo.aba } });
};
const RESUMO_MAX = 3;
const protecoes = computed(() => assistant.value?.guardrails || []);
const diretrizes = computed(() => assistant.value?.response_guidelines || []);
const rotaDoAssistente = name => ({
  name,
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
});

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...updatedAssistant,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// SiteUp (#6): clona o agente como template de outro nicho. Cria a cópia no
// backend, recarrega a lista e leva o usuário pra editar a cópia.
const handleClone = async () => {
  if (isCloning.value) return;

  isCloning.value = true;
  try {
    const { data } = await CaptainAssistantAPI.clone(assistantId.value);
    await store.dispatch('captainAssistants/get');
    useAlert('Agente clonado! Abrindo a cópia para você editar.');
    if (data?.id) {
      router.push({
        name: 'captain_assistants_settings_index',
        params: {
          accountId: route.params.accountId,
          assistantId: data.id,
        },
      });
    }
  } catch {
    // Sem o texto técnico do axios ("Request failed with status code 500").
    useAlert('Não foi possível clonar o agente.');
  } finally {
    isCloning.value = false;
  }
};

const handleDelete = () => {
  deleteAssistantDialog.value.dialogRef.open();
};

const handleDeleteSuccess = () => {
  // Get remaining assistants after deletion
  const remainingAssistants = assistants.value.filter(
    a => a.id !== assistantId.value
  );

  if (remainingAssistants.length > 0) {
    // Navigate to the first available assistant's settings
    const nextAssistant = remainingAssistants[0];
    router.push({
      name: 'captain_assistants_settings_index',
      params: {
        accountId: route.params.accountId,
        assistantId: nextAssistant.id,
      },
    });
  } else {
    // No assistants left, redirect to create assistant page
    router.push({
      name: 'captain_assistants_create_index',
      params: { accountId: route.params.accountId },
    });
  }
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :class="{
      '[&>header>div]:max-w-[60rem] [&>main>div]:max-w-[60rem]':
        isCaptainV2Enabled,
    }"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="indiceAbaAtiva"
          data-testid="assistant-abas"
          @tab-changed="trocarAba"
        />

        <!-- Aba 1 · Quem é -->
        <div
          v-if="abaAtiva === 'quem-e'"
          class="flex flex-col gap-8"
          data-testid="aba-quem-e"
        >
          <!-- SiteUp: configurar com ajuda de IA (meta-prompt pra IA externa) -->
          <div
            class="flex items-center justify-between gap-4 rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="flex items-start gap-3 min-w-0">
              <span
                class="i-ri-sparkling-line size-5 shrink-0 mt-0.5 text-n-brand"
              />
              <div class="flex flex-col gap-1 min-w-0">
                <h6 class="text-n-slate-12 text-base font-medium">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.CONFIG_IA.TITLE') }}
                </h6>
                <span class="text-n-slate-11 text-sm">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.CONFIG_IA.DESCRIPTION') }}
                </span>
              </div>
            </div>
            <div class="flex-shrink-0">
              <Button
                label="Configurar com IA"
                icon="i-ri-sparkling-line"
                class="!w-fit"
                @click="handleConfigWithAi"
              />
            </div>
          </div>

          <OnboardingChecklist v-if="assistant" :assistant="assistant" />
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.QUEM_E.IDENTIDADE.TITULO')"
              :description="t('CAPTAIN.QUEM_E.IDENTIDADE.DESCRICAO')"
            />
            <AssistantBasicSettingsForm
              :assistant="assistant"
              @submit="handleSubmit"
            />
          </div>
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <header class="flex flex-col items-start gap-2">
              <h2
                class="flex items-center gap-2 text-base font-medium text-n-slate-12"
              >
                {{ t('CAPTAIN.QUEM_E.BRIEFING.TITULO') }}
                <DicaDoCampo termo="BRIEFING" />
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.QUEM_E.BRIEFING.DESCRICAO') }}
              </p>
            </header>
            <AssistantSystemSettingsForm
              :assistant="assistant"
              secao="identidade"
              @submit="handleSubmit"
            />
          </div>
          <span class="h-px w-full bg-n-weak" />
          <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div
              class="flex flex-col gap-3 rounded-2xl border border-n-weak p-4"
              data-testid="resumo-jeito"
            >
              <div class="flex items-center gap-2">
                <h6 class="text-base font-medium text-n-slate-12">
                  {{ t('CAPTAIN.QUEM_E.JEITO.TITULO') }}
                </h6>
                <DicaDoCampo termo="JEITO_DE_FALAR" />
              </div>
              <span class="text-sm text-n-slate-11">{{
                t('CAPTAIN.QUEM_E.JEITO.DESCRICAO')
              }}</span>
              <ul
                v-if="diretrizes.length"
                class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-n-slate-12"
              >
                <li
                  v-for="(item, i) in diretrizes.slice(0, RESUMO_MAX)"
                  :key="i"
                >
                  {{ item }}
                </li>
              </ul>
              <p v-else class="m-0 text-sm text-n-slate-11">
                {{ t('CAPTAIN.QUEM_E.JEITO.VAZIO') }}
              </p>
              <router-link
                :to="rotaDoAssistente('captain_assistants_guidelines_index')"
                class="text-sm font-medium text-n-brand"
              >
                {{
                  diretrizes.length
                    ? t('CAPTAIN.QUEM_E.JEITO.VER_TUDO')
                    : t('CAPTAIN.QUEM_E.JEITO.ADICIONAR')
                }}
              </router-link>
            </div>
            <div
              class="flex flex-col gap-3 rounded-2xl border border-n-weak p-4"
              data-testid="resumo-protecoes"
            >
              <div class="flex items-center gap-2">
                <h6 class="text-base font-medium text-n-slate-12">
                  {{ t('CAPTAIN.QUEM_E.PROTECOES.TITULO') }}
                </h6>
                <DicaDoCampo termo="PROTECAO" />
              </div>
              <span class="text-sm text-n-slate-11">{{
                t('CAPTAIN.QUEM_E.PROTECOES.DESCRICAO')
              }}</span>
              <ul
                v-if="protecoes.length"
                class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-n-slate-12"
              >
                <li
                  v-for="(item, i) in protecoes.slice(0, RESUMO_MAX)"
                  :key="i"
                >
                  {{ item }}
                </li>
              </ul>
              <p v-else class="m-0 text-sm text-n-slate-11">
                {{ t('CAPTAIN.QUEM_E.PROTECOES.VAZIO') }}
              </p>
              <span
                v-if="protecoes.length > RESUMO_MAX"
                class="text-xs text-n-slate-11"
              >
                {{
                  t('CAPTAIN.QUEM_E.PROTECOES.MAIS', {
                    n: protecoes.length - RESUMO_MAX,
                  })
                }}
              </span>
              <router-link
                :to="rotaDoAssistente('captain_assistants_guardrails_index')"
                class="text-sm font-medium text-n-brand"
              >
                {{
                  protecoes.length
                    ? t('CAPTAIN.QUEM_E.PROTECOES.VER_TUDO')
                    : t('CAPTAIN.QUEM_E.PROTECOES.ADICIONAR')
                }}
              </router-link>
            </div>
          </div>
          <p v-if="isAdmin" class="m-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.QUEM_E.TECNICO_DICA') }}
          </p>
        </div>

        <!-- Aba 2 · Onde atua -->

        <AbaOndeAtua
          v-else-if="abaAtiva === 'onde-atua'"
          :assistant-id="assistantId"
          :assistant="assistant"
          @submit="handleSubmit"
        />

        <!-- Aba 3 · O que pode fazer -->

        <AbaOQuePodeFazer
          v-else-if="abaAtiva === 'o-que-pode-fazer'"
          :assistant-id="assistantId"
          :assistant="assistant"
        />

        <!-- Aba 4 · O que sabe -->

        <AbaOQueSabe
          v-else-if="abaAtiva === 'o-que-sabe'"
          :assistant-id="assistantId"
          :assistant="assistant"
        />

        <!-- Aba 5 · Técnico (administrador) -->
        <div
          v-else-if="abaAtiva === 'tecnico' && isAdmin"
          class="flex flex-col gap-8"
          data-testid="aba-tecnico"
        >
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.TITLE')"
              :description="
                t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.DESCRIPTION')
              "
            />
            <AssistantSystemSettingsForm
              :assistant="assistant"
              secao="tecnico"
              @submit="handleSubmit"
            />
          </div>
          <template v-if="isCaptainV2Enabled">
            <span class="h-px w-full bg-n-weak" />
            <div class="flex flex-col gap-6">
              <SettingsHeader
                :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.TITLE')"
                :description="
                  t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.DESCRIPTION')
                "
              />
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AssistantControlItems
                  v-for="item in controlItems"
                  :key="item.name"
                  :control-item="item"
                />
              </div>
            </div>
          </template>
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              heading="Ações do agente"
              description="Clonar este agente como base para outro, ou removê-lo da conta."
            />
            <!-- SiteUp (#6): clonar o agente como template de outro nicho/cliente -->
            <div class="flex items-end justify-between w-full gap-4">
              <div class="flex flex-col gap-2">
                <h6 class="text-n-slate-12 text-base font-medium">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.CLONE.TITLE') }}
                </h6>
                <span class="text-n-slate-11 text-sm">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.CLONE.DESCRIPTION') }}
                </span>
              </div>
              <div class="flex-shrink-0">
                <Button
                  label="Clonar agente"
                  color="slate"
                  icon="i-ri-file-copy-line"
                  class="max-w-56 !w-fit"
                  :is-loading="isCloning"
                  :disabled="isCloning"
                  @click="handleClone"
                />
              </div>
            </div>
            <div class="flex items-end justify-between w-full gap-4">
              <div class="flex flex-col gap-2">
                <h6 class="text-n-slate-12 text-base font-medium">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.TITLE') }}
                </h6>
                <span class="text-n-slate-11 text-sm">
                  {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.DESCRIPTION') }}
                </span>
              </div>
              <div class="flex-shrink-0">
                <Button
                  :label="
                    t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.BUTTON_TEXT', {
                      assistantName: assistant.name,
                    })
                  "
                  color="ruby"
                  class="max-w-56 !w-fit"
                  @click="handleDelete"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <DeleteDialog
      v-if="assistant"
      ref="deleteAssistantDialog"
      :entity="assistant"
      type="Assistants"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
    <ConfigWithAiDialog
      v-if="assistant"
      ref="configWithAiDialog"
      :assistant="assistant"
    />
  </PageLayout>
</template>
